import React from 'react';

class DraftErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error details
        console.error('Draft.js Error:', error);
        console.error('Error Info:', errorInfo);
        
        // Check if it's a DOM manipulation error
        const isDOMError = error.message?.includes('removeChild') || 
                          error.message?.includes('not a child of this node') ||
                          error.name === 'NotFoundError';
        
        // Check if it's a nullthrows error from fbjs
        const isNullthrowsError = error.message?.includes('Got unexpected null or undefined') ||
                                 error.stack?.includes('nullthrows') ||
                                 error.stack?.includes('fbjs/lib/nullthrows');
        
        // Check if it's an obfuscation error
        const isObfuscationError = error.name === 'ReferenceError' && 
                                  error.message && 
                                  /_0x[a-f0-9]+ is not defined/.test(error.message);
        
        // Check if it's a Draft.js internal state error
        const isDraftInternalError = error.message?.includes('getCharacterList') ||
                                   error.message?.includes('removeForBlock') ||
                                   error.message?.includes('removeEntitiesAtEdges') ||
                                   error.message?.includes('getIn') ||
                                   error.message?.includes('getBlockTree') ||
                                   error.message?.includes('getLength') ||
                                   error.stack?.includes('draft-js/lib/removeEntitiesAtEdges') ||
                                   error.stack?.includes('DraftEditorCompositionHandler') ||
                                   error.stack?.includes('insertFragmentIntoContentState');
        
        if (isDOMError) {
            console.warn('DOM manipulation error detected in Draft.js. This is often caused by React/DOM synchronization issues.');
        }
        
        if (isNullthrowsError) {
            console.warn('Nullthrows error detected in Draft.js. This is often caused by null/undefined values being passed to Draft.js internals.');
        }
        
        if (isObfuscationError) {
            console.warn('Obfuscation error detected. This is often caused by minified/obfuscated code with undefined variables.');
        }
        
        if (isDraftInternalError) {
            if (error.message?.includes('getIn')) {
                console.warn('Draft.js getIn error detected. This is often caused by undefined getBlockTree results in composition handler.');
            } else if (error.message?.includes('getBlockTree')) {
                console.warn('Draft.js getBlockTree error detected. This is often caused by invalid block keys or corrupted editor state.');
            } else if (error.message?.includes('getLength')) {
                console.warn('Draft.js getLength error detected. This is often caused by undefined block objects in fragment operations.');
            } else if (error.stack?.includes('insertFragmentIntoContentState')) {
                console.warn('Draft.js fragment insertion error detected. This is often caused by undefined blocks during fragment operations.');
            } else {
                console.warn('Draft.js internal state error detected. This is often caused by corrupted editor state or undefined blocks.');
            }
        }
        
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Log to Sentry if available
        if (window.Sentry) {
            window.Sentry.captureException(error, {
                contexts: {
                    react: {
                        componentStack: errorInfo.componentStack
                    }
                },
                tags: {
                    component: 'DraftEditor',
                    errorBoundary: 'DraftErrorBoundary',
                    errorType: isDOMError ? 'DOM_MANIPULATION' : 
                              isNullthrowsError ? 'NULLTHROWS' : 
                              isObfuscationError ? 'OBFUSCATION' :
                              isDraftInternalError ? 'DRAFT_INTERNAL' : 'GENERAL'
                }
            });
        }
    }

    render() {
        if (this.state.hasError) {
            // Fallback UI
            return (
                <div style={{
                    padding: '20px',
                    border: '1px solid #ff6b6b',
                    borderRadius: '8px',
                    backgroundColor: '#fff5f5',
                    margin: '10px 0'
                }}>
                    <h3 style={{ color: '#d63031', margin: '0 0 10px 0' }}>
                        Editor Error
                    </h3>
                    <p style={{ margin: '0 0 10px 0', color: '#636e72' }}>
                        There was an error with the text editor. Please try refreshing the page.
                    </p>
                    {process.env.NODE_ENV === 'development' && (
                        <details style={{ marginTop: '10px' }}>
                            <summary style={{ cursor: 'pointer', color: '#74b9ff' }}>
                                Error Details (Development)
                            </summary>
                            <pre style={{ 
                                marginTop: '10px', 
                                padding: '10px', 
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                overflow: 'auto',
                                fontSize: '12px'
                            }}>
                                {this.state.error && this.state.error.toString()}
                                {this.state.errorInfo.componentStack}
                            </pre>
                        </details>
                    )}
                    <button 
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: '10px',
                            padding: '8px 16px',
                            backgroundColor: '#74b9ff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default DraftErrorBoundary;
